import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  archiveAcademicProgramMutation,
  archiveGradeMutation,
  createAcademicProgramQuery,
  createAcademicStreamQuery,
  createBillingPeriodQuery,
  createDepartmentQuery,
  createGradeQuery,
  createStudentCategoryQuery,
  listAcademicProgramsQuery,
  listAcademicStreamsQuery,
  listBillingPeriodsQuery,
  listDepartmentsQuery,
  listGradesQuery,
  listStudentCategoriesQuery,
  updateAcademicProgramMutation
} from "../../../Graphql/querries/education/academicQuery";
import {
  AcademicProgram,
  AcademicStream,
  ArchiveAcademicProgramInput,
  ArchiveAcademicProgramResponse,
  ArchiveGradeInput,
  ArchiveGradeResponse,
  BillingPeriod,
  CreateAcademicProgramInput,
  CreateAcademicProgramResponse,
  CreateAcademicStreamInput,
  CreateAcademicStreamResponse,
  CreateBillingPeriodInput,
  CreateBillingPeriodResponse,
  CreateDepartmentInput,
  CreateDepartmentResponse,
  CreateGradeInput,
  CreateGradeResponse,
  CreateStudentCategoryInput,
  CreateStudentCategoryResponse,
  Department,
  Grade,
  ListAcademicProgramsResponse,
  ListAcademicStreamsInput,
  ListAcademicStreamsResponse,
  ListBillingPeriodsInput,
  ListBillingPeriodsResponse,
  ListDepartmentsResponse,
  ListGradesInput,
  ListGradesResponse,
  ListStudentCategoriesResponse,
  StudentCategory,
  UpdateAcademicProgramInput,
  UpdateAcademicProgramResponse
} from "../../../components/Education/Academic/types/academic";
import { GraphQLData, unwrapGraphQLOperation } from "./educationGraphQLUtils";

export const useAcademicPrograms = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["academicPrograms"],
    queryFn: async (): Promise<AcademicProgram[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListAcademicProgramsResponse, "listAcademicPrograms">,
        Record<string, never>
      >(listAcademicProgramsQuery, {}, setAlert);
      const response = unwrapGraphQLOperation(data, "listAcademicPrograms");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.programs ?? [];
    }
  });
};

export const useCreateAcademicProgram = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateAcademicProgramInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateAcademicProgramResponse, "createAcademicProgram">,
        { input: CreateAcademicProgramInput }
      >(createAcademicProgramQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createAcademicProgram");
      if (response.code !== API_RESPONSE_CODE.OK || !response.program) {
        throw new Error(response.code);
      }
      return response.program;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useUpdateAcademicProgram = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: UpdateAcademicProgramInput) => {
      const data = await fetchGraphQL<
        GraphQLData<UpdateAcademicProgramResponse, "updateAcademicProgram">,
        { input: UpdateAcademicProgramInput }
      >(updateAcademicProgramMutation, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "updateAcademicProgram");
      if (response.code !== API_RESPONSE_CODE.OK || !response.program) {
        throw new Error(response.code);
      }
      return response.program;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

const fetchGradesForProgram = async (
  programId: string,
  setAlert: (alert: unknown) => void
): Promise<Grade[]> => {
  const data = await fetchGraphQL<
    GraphQLData<ListGradesResponse, "listGrades">,
    { input: ListGradesInput }
  >(listGradesQuery, { input: { programId } }, setAlert);
  const response = unwrapGraphQLOperation(data, "listGrades");
  if (response.code !== API_RESPONSE_CODE.OK) {
    return [];
  }
  return response.grades ?? [];
};

const fetchStreamsForGrade = async (
  gradeId: string,
  setAlert: (alert: unknown) => void
): Promise<AcademicStream[]> => {
  const data = await fetchGraphQL<
    GraphQLData<ListAcademicStreamsResponse, "listAcademicStreams">,
    { input: ListAcademicStreamsInput }
  >(listAcademicStreamsQuery, { input: { gradeId } }, setAlert);
  const response = unwrapGraphQLOperation(data, "listAcademicStreams");
  if (response.code !== API_RESPONSE_CODE.OK) {
    return [];
  }
  return response.streams ?? [];
};

export const useGrades = (programId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["grades", programId ?? ""],
    enabled: Boolean(programId),
    queryFn: async (): Promise<Grade[]> =>
      fetchGradesForProgram(programId!, setAlert)
  });
};

/** Grades across all programs, or filtered to one program when programId is set. */
export const useAllGrades = (programId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { data: programs = [], isPending: programsLoading } =
    useAcademicPrograms();
  const programIds = programId
    ? [programId]
    : programs.map((program) => program.id);

  const gradeQueries = useQueries({
    queries: programIds.map((id) => ({
      queryKey: ["grades", id],
      enabled: Boolean(id) && !programsLoading,
      queryFn: () => fetchGradesForProgram(id, setAlert)
    }))
  });

  const grades = gradeQueries.flatMap((query) => query.data ?? []);
  const isPending =
    programsLoading || gradeQueries.some((query) => query.isPending);

  return {
    data: grades,
    isPending,
    refetch: () => gradeQueries.forEach((q) => q.refetch())
  };
};

export const useCreateGrade = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateGradeInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateGradeResponse, "createGrade">,
        { input: CreateGradeInput }
      >(createGradeQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createGrade");
      if (response.code !== API_RESPONSE_CODE.OK || !response.grade) {
        throw new Error(response.code);
      }
      return response.grade;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useAcademicStreams = (gradeId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["academicStreams", gradeId ?? ""],
    enabled: Boolean(gradeId),
    queryFn: async (): Promise<AcademicStream[]> =>
      fetchStreamsForGrade(gradeId!, setAlert)
  });
};

/** Streams across grades (optionally filtered to one grade). */
export const useAllStreams = (gradeId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { data: grades = [], isPending: gradesLoading } = useAllGrades();
  const gradeIds = gradeId ? [gradeId] : grades.map((grade) => grade.id);

  const streamQueries = useQueries({
    queries: gradeIds.map((id) => ({
      queryKey: ["academicStreams", id],
      enabled: Boolean(id) && !gradesLoading,
      queryFn: () => fetchStreamsForGrade(id, setAlert)
    }))
  });

  const streams = streamQueries.flatMap((query) => query.data ?? []);
  const isPending =
    gradesLoading || streamQueries.some((query) => query.isPending);

  return {
    data: streams,
    isPending,
    refetch: () => streamQueries.forEach((q) => q.refetch())
  };
};

export const useCreateAcademicStream = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateAcademicStreamInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateAcademicStreamResponse, "createAcademicStream">,
        { input: CreateAcademicStreamInput }
      >(createAcademicStreamQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createAcademicStream");
      if (response.code !== API_RESPONSE_CODE.OK || !response.stream) {
        throw new Error(response.code);
      }
      return response.stream;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useBillingPeriods = (input?: ListBillingPeriodsInput) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["billingPeriods", input?.type ?? "ALL"],
    queryFn: async (): Promise<BillingPeriod[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListBillingPeriodsResponse, "listBillingPeriods">,
        { input: ListBillingPeriodsInput | Record<string, never> }
      >(listBillingPeriodsQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listBillingPeriods");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.billingPeriods ?? [];
    }
  });
};

export const useCreateBillingPeriod = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateBillingPeriodInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateBillingPeriodResponse, "createBillingPeriod">,
        { input: CreateBillingPeriodInput }
      >(createBillingPeriodQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createBillingPeriod");
      if (response.code !== API_RESPONSE_CODE.OK || !response.billingPeriod) {
        throw new Error(response.code);
      }
      return response.billingPeriod;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useDepartments = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["departments"],
    queryFn: async (): Promise<Department[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListDepartmentsResponse, "listDepartments">,
        Record<string, never>
      >(listDepartmentsQuery, {}, setAlert);
      const response = unwrapGraphQLOperation(data, "listDepartments");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.departments ?? [];
    }
  });
};

export const useCreateDepartment = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateDepartmentInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateDepartmentResponse, "createDepartment">,
        { input: CreateDepartmentInput }
      >(createDepartmentQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createDepartment");
      if (response.code !== API_RESPONSE_CODE.OK || !response.department) {
        throw new Error(response.code);
      }
      return response.department;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useStudentCategories = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["studentCategories"],
    queryFn: async (): Promise<StudentCategory[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListStudentCategoriesResponse, "listStudentCategories">,
        Record<string, never>
      >(listStudentCategoriesQuery, {}, setAlert);
      const response = unwrapGraphQLOperation(data, "listStudentCategories");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.studentCategories ?? [];
    }
  });
};

export const useCreateStudentCategory = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateStudentCategoryInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateStudentCategoryResponse, "createStudentCategory">,
        { input: CreateStudentCategoryInput }
      >(createStudentCategoryQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createStudentCategory");
      if (response.code !== API_RESPONSE_CODE.OK || !response.studentCategory) {
        throw new Error(response.code);
      }
      return response.studentCategory;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useArchiveAcademicProgram = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: ArchiveAcademicProgramInput) => {
      const data = await fetchGraphQL<
        GraphQLData<ArchiveAcademicProgramResponse, "archiveAcademicProgram">,
        { input: ArchiveAcademicProgramInput }
      >(archiveAcademicProgramMutation, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "archiveAcademicProgram");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
      return response.program;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useArchiveGrade = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: ArchiveGradeInput) => {
      const data = await fetchGraphQL<
        GraphQLData<ArchiveGradeResponse, "archiveGrade">,
        { input: ArchiveGradeInput }
      >(archiveGradeMutation, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "archiveGrade");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
      return response.grade;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};
